import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ScheduledReportsEditModalComponent } from 'dashboard/components/content-dashboard/scheduled-reports-dashboard-widget/modals';
import { ScheduledReportsRunStatusCellRendererComponent } from 'dashboard/components/content-dashboard/scheduled-reports-dashboard-widget/table-renderers';
import { ScheduledReportsFrequencyCellRendererComponent } from 'dashboard/components/content-dashboard/scheduled-reports-dashboard-widget/table-renderers/scheduled-reports-frequency-cell-renderer/scheduled-reports-frequency-cell-renderer.component';
import { ScheduledReportsReportTypeCellRendererComponent } from 'dashboard/components/content-dashboard/scheduled-reports-dashboard-widget/table-renderers/scheduled-reports-report-type-cell-renderer/scheduled-reports-report-type-cell-renderer.component';
import { scheduledReportTypeTranslationMap } from 'dashboard/constants/scheduled-report-type-translation-map.constant';
import { ScheduledReportType } from 'dashboard/enums/scheduled-report-type.enum';
import { ScheduledReport } from 'dashboard/interfaces/scheduled-report.interface';
import { DashboardService } from 'dashboard/services/dashboard.service';
import { filter, switchMap } from 'rxjs';
import { DataGridDateCellRendererComponent } from 'shared/components/data-grid/data-grid-date-cell-renderer/data-grid-date-cell-renderer.component';
import { DataGridComponent } from 'shared/components/data-grid/data-grid.component';
import { DefaultDataGridActionCellRendererComponent } from 'shared/components/data-grid/default-data-grid-action-cell-renderer/default-data-grid-action-cell-renderer.component';
import { DataGridColumnDefinition } from 'shared/interfaces/data-grid.interface';
import { ModalService, ToastService } from 'shared/services';
import { SharedModule } from 'shared/shared.module';

@Component({
    selector: 'app-scheduled-reports-dashboard-widget',
    templateUrl: './scheduled-reports-dashboard-widget.component.html',
    styleUrls: ['./scheduled-reports-dashboard-widget.component.scss'],
    imports: [TranslateModule, SharedModule, DataGridComponent],
})
export class ScheduledReportsDashboardWidgetComponent implements OnInit {
    public columns?: DataGridColumnDefinition[];
    public data: ScheduledReport[] = [];

    constructor(
        private destroyRef: DestroyRef,
        private modalService: ModalService,
        private toastService: ToastService,
        private dashboardService: DashboardService,
        private translate: TranslateService,
    ) {}

    public ngOnInit(): void {
        this.generateColumnDefinitions();
        this.getWidgetData();
    }

    private generateColumnDefinitions() {
        this.columns = [
            {
                headerLabel: 'dashboard.scheduledReports.table.reportName',
                dataKey: 'reportName',
                sortable: true,
                sortDataPreprocessor: (value: ScheduledReportType) => scheduledReportTypeTranslationMap[value],
                customCellRenderer: ScheduledReportsReportTypeCellRendererComponent,
            },
            {
                headerLabel: 'dashboard.scheduledReports.table.schedule',
                dataKey: 'scheduleType',
                customCellRenderer: ScheduledReportsFrequencyCellRendererComponent,
            },
            {
                headerLabel: 'dashboard.scheduledReports.table.lastRunTime',
                dataKey: 'lastRunTime',
                customCellRenderer: DataGridDateCellRendererComponent,
                customCellRendererData: {
                    format: 'yyyy-MM-dd HH:mm:ss',
                },
                sortable: true,
            },
            {
                headerLabel: 'dashboard.scheduledReports.table.lastRunStatus',
                dataKey: 'lastRunStatus',
                customCellRenderer: ScheduledReportsRunStatusCellRendererComponent,
                sortable: true,
            },
            {
                headerLabel: 'dashboard.scheduledReports.table.nextRunTime',
                dataKey: 'nextRunTime',
                customCellRenderer: DataGridDateCellRendererComponent,
                customCellRendererData: {
                    format: 'yyyy-MM-dd HH:mm:ss',
                },
                sortable: true,
            },
            {
                headerLabel: '',
                customCellRenderer: DefaultDataGridActionCellRendererComponent,
                customCellRendererData: [
                    {
                        label: 'dynamicForm.table.actions.edit',
                        icon: 'pen-to-square',
                        clickHandler: this.openAddScheduledReportModal.bind(this),
                    },
                    {
                        label: 'dynamicForm.table.actions.delete',
                        icon: 'trash',
                        clickHandler: this.handleDelete.bind(this),
                    },
                    {
                        label: 'dynamicForm.table.actions.run',
                        icon: 'play',
                        clickHandler: this.handleRun.bind(this),
                    },
                ],
            },
        ];
    }

    private getWidgetData() {
        this.dashboardService
            .getScheduledReports()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: ScheduledReport[]) => {
                this.data = data;
            });
    }

    public handleDelete(data: ScheduledReport) {
        this.modalService
            .openDeleteConfirmationModal(
                undefined,
                undefined,
                undefined,
                this.translate.instant(scheduledReportTypeTranslationMap[data.reportName]),
                'dashboard.scheduledReports.table.reportName',
            )
            .onClose.pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(Boolean),
                switchMap(() => {
                    return this.dashboardService.deleteScheduledReport(data.id);
                }),
            )
            .subscribe(() => {
                this.toastService.showToast('dashboard.scheduledReports.toast.deleteSuccess', 'success');
                this.getWidgetData();
            });
    }

    public handleRun(data: ScheduledReport) {
        this.dashboardService
            .runScheduledReport(data.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.toastService.showToast('dashboard.scheduledReports.toast.runSuccess', 'success');
                this.getWidgetData();
            });
    }

    public openAddScheduledReportModal(report?: any) {
        const modalData = { ...(report ?? {}), type: 'center' };

        this.modalService
            .openModal(ScheduledReportsEditModalComponent, modalData)
            .onClose.pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean))
            .subscribe(() => {
                this.getWidgetData();
            });
    }
}
